/**
 * Payment Success Page
 *
 * After Stripe checkout, agents land here.
 * Verifies the proposal status and redirects to signup.
 */

import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

type ProposalStatus = "loading" | "success" | "success_existing" | "pending_confirmation" | "error" | "already_used";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ProposalStatus>("loading");
  const [proposalData, setProposalData] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [ready, setReady] = useState(false);
  const [isExistingCustomer, setIsExistingCustomer] = useState(false);

  const proposalId = searchParams.get("proposal_id");

  // Sign out existing session ONLY if the logged-in email doesn't match the proposal.
  // If the user is already logged in with the correct email, preserve the session.
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session && proposalId) {
        // Fetch proposal email to check if it matches
        const { data: proposal } = await supabase.rpc("get_proposal_public", {
          proposal_id: proposalId,
        }) as any;

        if (proposal?.agent_email && session.user.email === proposal.agent_email) {
          // Same user — preserve session, they're an existing customer
          setIsExistingCustomer(true);
          setReady(true);
          return;
        }
        // Different user — sign out
        await supabase.auth.signOut();
      }
      setReady(true);
    });
  }, [proposalId]);

  useEffect(() => {
    if (!ready) return;
    if (!proposalId) {
      setStatus("error");
      setErrorMessage("Missing proposal ID.");
      return;
    }

    let intervalId: ReturnType<typeof setInterval> | null = null;
    let cancelled = false;

    const checkProposal = async () => {
      const { data, error } = await supabase.rpc("get_proposal_public", {
        proposal_id: proposalId,
      }) as any;

      if (cancelled) return;

      if (error || !data) {
        setStatus("error");
        setErrorMessage("Proposal not found. Please contact support.");
        return;
      }

      if (data.status === "signup_complete") {
        setStatus("already_used");
        return;
      }

      if (data.status === "paid" || data.status === "contract_created") {
        setProposalData(data);

        // Check if this email belongs to an existing agent (existing customer buying new package)
        if (data.agent_email && !isExistingCustomer) {
          const { data: existingAgent } = await supabase.
          from("agents").
          select("id, name").
          eq("email", data.agent_email).
          maybeSingle();

          if (existingAgent) {
            setIsExistingCustomer(true);
            setStatus("success_existing");
            return;
          }
        }

        if (isExistingCustomer) {
          setStatus("success_existing");
          return;
        }

        setStatus("success");
        return;
      }

      // Payment might still be processing — poll a few times
      let attempts = 0;
      intervalId = setInterval(async () => {
        if (cancelled) {
          if (intervalId) clearInterval(intervalId);
          return;
        }
        attempts++;
        const { data: updated } = await supabase.rpc("get_proposal_public", {
          proposal_id: proposalId,
        }) as any;

        if (cancelled) return;

        if (updated && (updated.status === "paid" || updated.status === "contract_created")) {
          if (intervalId) clearInterval(intervalId);
          setStatus("success");
          setProposalData(updated);
        } else if (attempts >= 10) {
          if (intervalId) clearInterval(intervalId);
          setStatus("pending_confirmation");
          setProposalData(updated || data);
        }
      }, 2000);
    };

    checkProposal();

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [proposalId, ready]);

  const handleContinueToSignup = () => {
    navigate(`/signup?proposal_id=${proposalId}`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-8">
      <div className="w-full max-w-md text-center">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <img alt="Trusted James" className="h-10 w-auto" src="/lovable-uploads/3379ec2c-12ea-47a0-af19-2323a0fdc290.png" />
        </Link>

        {(status === "loading" || !ready) &&
        <div className="space-y-4">
            <Loader2 className="w-12 h-12 text-accent animate-spin mx-auto" />
            <h1 className="font-display text-2xl font-bold text-foreground">
              Confirming your payment...
            </h1>
            <p className="text-muted-foreground">
              Please wait while we verify your payment with Stripe.
            </p>
          </div>
        }

        {status === "success" &&
        <div className="space-y-6">
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-foreground mb-2">
                Payment Confirmed!
              </h1>
              <p className="text-muted-foreground">
                Thank you{proposalData?.agent_name ? `, ${proposalData.agent_name}` : ""}! Your payment has been received.
              </p>
            </div>

            {proposalData &&
          <div className="rounded-xl border bg-card p-4 text-left space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Package</span>
                  <span className="font-medium">{proposalData.total_leads} leads</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Vertical</span>
                  <span className="font-medium capitalize">
                    {proposalData.vertical === "life_insurance" ? "Life Insurance" : "Mortgage"}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-medium">${proposalData.total_amount}</span>
                </div>
              </div>
          }

            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Next step: Create your account to start receiving leads.
              </p>
              <Button
              variant="gold"
              size="lg"
              className="w-full"
              onClick={handleContinueToSignup}>

                Create Your Account
              </Button>
            </div>
          </div>
        }

        {status === "success_existing" &&
        <div className="space-y-6">
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-foreground mb-2">
                Welcome back{proposalData?.agent_name ? `, ${proposalData.agent_name}` : ""}!
              </h1>
              <p className="text-muted-foreground">
                Your payment has been received and your new contract is ready.
              </p>
            </div>

            {proposalData &&
          <div className="rounded-xl border bg-card p-4 text-left space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Package</span>
                  <span className="font-medium">{proposalData.total_leads} leads</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Vertical</span>
                  <span className="font-medium capitalize">
                    {proposalData.vertical === "life_insurance" ? "Life Insurance" : "Mortgage"}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-medium">${proposalData.total_amount}</span>
                </div>
              </div>
          }

            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Your new contract has been automatically linked to your account.
              </p>
              <Button
              variant="gold"
              size="lg"
              className="w-full"
              onClick={() => navigate("/dashboard/contract-delivery")}>

                Go to Dashboard
              </Button>
              <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => navigate("/login")}>

                Sign in with a different account
              </Button>
            </div>
          </div>
        }

        {status === "pending_confirmation" &&
        <div className="space-y-6">
            <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto">
              <Clock className="w-10 h-10 text-accent" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-foreground mb-2">
                Your payment is being processed
              </h1>
              <p className="text-muted-foreground">
                This may take a few moments.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                If you completed payment, you can proceed to create your account. Your contract will be activated automatically.
              </p>
            </div>

            {proposalData &&
          <div className="rounded-xl border bg-card p-4 text-left space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Package</span>
                  <span className="font-medium">{proposalData.total_leads} leads</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Vertical</span>
                  <span className="font-medium capitalize">
                    {proposalData.vertical === "life_insurance" ? "Life Insurance" : "Mortgage"}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-medium">${proposalData.total_amount}</span>
                </div>
              </div>
          }

            <div className="space-y-3">
              <Button
              variant="gold"
              size="lg"
              className="w-full"
              onClick={handleContinueToSignup}>

                Continue to Account Setup
              </Button>
              <p className="text-xs text-muted-foreground">
                Payment confirmation is still processing in the background.
              </p>
            </div>
          </div>
        }

        {status === "already_used" &&
        <div className="space-y-4">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-foreground">
              Account Already Created
            </h1>
            <p className="text-muted-foreground">
              This payment link has already been used. You can sign in to your dashboard.
            </p>
            <Button variant="gold" size="lg" className="w-full" onClick={() => navigate("/login")}>
              Sign In
            </Button>
          </div>
        }

        {status === "error" &&
        <div className="space-y-4">
            <AlertTriangle className="w-12 h-12 text-destructive mx-auto" />
            <h1 className="font-display text-2xl font-bold text-foreground">
              Something went wrong
            </h1>
            <p className="text-muted-foreground">{errorMessage}</p>
            <Button variant="outline" onClick={() => navigate("/")}>
              Go Home
            </Button>
          </div>
        }
      </div>
    </div>);

};

export default PaymentSuccess;